import { PlatformLocation } from '@angular/common';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AutocompleteComponent } from './autocomplete.component';
import { SearchInputHelperService } from './search-input-helper.service';
import {
  AutocompleteSearchResponse,
  AutocompleteSelectedItem,
  AutocompleteSettings,
  AutocompleteUiItem,
  SearchRequest,
} from './search-input.model';
import { SearchInputService } from './search-input.service';
import { WebApiModel } from '../../models/web-api.model';
import { GoogleAnalyticsService } from '../../services/google-analytics.service';
import { RouterHelperService, RouteUrl } from '../../services/router-helper.service';
import { SettingsService } from '../../services/settings.service';
import { StateService } from '../../services/state.service';
import { StateRxService } from '../../services/staterx.service';
import { UiService } from '../../services/ui.service';
import { MapService } from '../maps/healthcarefinder-map.service';

declare let navigator: any;
declare let google: any;

@Component({
  selector: 'hcf-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SearchInputComponent implements OnInit {
  @ViewChild('keywordsAutocompleteComponent')
  keywordsAutocompleteComponent: AutocompleteComponent;
  @ViewChild('locationAutocompleteComponent')
  locationAutocompleteComponent: AutocompleteComponent;

  // Store current user input coming from the autocomplete components.
  private _keywordInput: string;
  private _defaultCategory: string;
  private _locationInput: string;
  private readonly _inputDelay: number = 300;
  private readonly _inputMinChar: number = 2;
  private _myLocationLat: string = null;
  private _myLocationLng: string = null;

  usingMyLocation: boolean = false;
  lookingForLocation: boolean = false;
  keywordAutocompleteData: AutocompleteUiItem[];
  locationAutocompleteData: AutocompleteUiItem[];
  myLocationLabel: string = SettingsService.autoCompleteValues.useMyLocationText;
  myLocationFound: boolean = false;
  failedMyLocation: boolean = false;
  // Search results type to pass the the search results page
  searchResultsType: string = null;

  keywordAutocompleteSettings: AutocompleteSettings = {
    inputDelay: this._inputDelay,
    inputMinChar: this._inputMinChar,
    inputPlaceholder: SettingsService.values.keywordsInputPlaceholder,
  };
  locationAutocompleteSettings: AutocompleteSettings = {
    inputDelay: this._inputDelay,
    inputMinChar: this._inputMinChar,
    inputPlaceholder: SettingsService.values.locationInputPlaceholder,
  };

  constructor(
    private _searchService: SearchInputService,
    private _router: Router,
    private _helperService: SearchInputHelperService,
    private _gaService: GoogleAnalyticsService,
    private _uiService: UiService,
    private _stateRxService: StateRxService,
    private _stateService: StateService,
    private _mapService: MapService,
    private _location: PlatformLocation,
  ) {
    _location.onPopState(() => {
      if (window.location.pathname === '/') {
        this.onHomeClick();
      }
    });
    this._stateRxService.enterPressed.rxSubscribe(async () => {
      await this.onKeywordsGetData();
    });
  }

  ngOnInit() {
    this._router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        // Reset the keywords input box after navigating to the home page.
        if (this._router.url === '/') {
          if (this.keywordsAutocompleteComponent) {
            this.keywordsAutocompleteComponent.resetInputValue('');
          }
        }
      }
    });

    this._uiService.bindWindowResize(() => {
      this.onWindowResize();
    });
  }

  onWindowResize(): void {
    this._uiService.drawCurveSvgHeader();
  }

  /**
   * Create a new item to bind to the UI based on the web api data. Add extra properties to the UI item.
   * @param item - Web api AutocompleteItem
   */
  buildAutocompleteItem(item: WebApiModel.AutocompleteItem, index: number): AutocompleteUiItem {
    return this._searchService.buildAutocompleteItem(item, index, this._keywordInput);
  }

  onAutocompleteKeywordSelected(item: AutocompleteSelectedItem) {
    // Autocomplete componenet returns a null item when nothing was selected from a dropdown and the Enter key was pressed
    // in this case we take user to the search results page
    if (item.autocompleteUiItem === null) {
      this.onFindClick();
      return;
    }

    // Update this component input with current up to date search input from the child, the child search input is passed with the item.
    this._keywordInput = item.searchInput;
    this._defaultCategory = item.autocompleteUiItem.data.defaultCategory;
    const location = this._locationInput
      ? this._locationInput
      : this._myLocationLat && this._myLocationLng
        ? this.myLocationLabel
        : null;

    if (this._keywordInput) {
      this._gaService.sendAutocompleteResultsEvent(this._keywordInput, location);
    }

    // If item is not clickable then don't do anything, for example the no results item.
    if (!item.autocompleteUiItem.isClickable) {
      return;
    }

    const route = this._helperService.getAutocompleteRoute(
      item.autocompleteUiItem.data,
      item.autocompleteUiItem.data.defaultCategory,
      item.searchTerm,
      this._locationInput,
      this._myLocationLat,
      this._myLocationLng,
      this.myLocationLabel,
    );

    if (item.autocompleteUiItem.isProfile) {
      // No query string for the profile pages
      void this._router.navigate(route.link);
      this._uiService.scrollToTop();
      return;
    }

    // Set the search type when item is selected from dropdown
    this.searchResultsType = this._helperService.getAutocompeteItemType(
      item.autocompleteUiItem.data.type,
    ).searchResultsType;

    // If location is empty and is not my location then set focus on the location
    if (!this._locationInput && !this.usingMyLocation && !this.lookingForLocation && !this.failedMyLocation) {
      this.locationAutocompleteComponent.giveInputFocus();
    } else {
      // This is triggered when "search all" is clicked from the auto-complete search results
      // Will save the query string collection to the newly added searchParameters and then later remove queryParams from navigate()
      this._stateService.setSearchQueryParameters(route.query);
      void this._router.navigate(route.link);
      this._uiService.scrollToTop();
    }
  }

  async onAutocompleteLocationSelected(item: AutocompleteSelectedItem) {
    this._locationInput = item.searchInput;
    const keywordInput = this._keywordInput ? this._keywordInput : null;

    if (this._locationInput) {
      this._gaService.sendLocationResultsEvent(keywordInput, this._locationInput);
    }

    // Fire normal search if item is null, it happens when the Enter key is pressed inside the location input.
    if (item.autocompleteUiItem === null) {
      this.onFindClick();
      return;
    }

    if (item.autocompleteUiItem.data.type === this._helperService.autocompleteItemType.MyLocation.type) {
      this.usingMyLocation = true;
      await this.getMyLocation();
      this.locationAutocompleteComponent.resetInputValue('');
      return;
    } else {
      this._myLocationLat = null;
      this._myLocationLng = null;
      this.usingMyLocation = false;
    }

    // Business requirement: submit search when location item is clicked and if search input is not empty
    if (this._keywordInput) {
      this.onFindClick();
    } else {
      // Business req: If location is selected but the keyword is empty then set focus to the keywords input
      this.keywordsAutocompleteComponent.giveInputFocus();
    }
  }

  /**
   * Handle Find button click event
   */
  onFindClick() {
    this._stateService.isSavingTopResult.pipe(filter((saving) => saving !== true)).subscribe((saving) => {
      // Don't submit the search if the keywords input is empty or less than min char
      if (!this._keywordInput || this._keywordInput.length < this._inputMinChar) {
        this.keywordsAutocompleteComponent.giveInputFocus();
        return;
      }

      if (!this._locationInput && !this.myLocationFound) {
        this._myLocationLat = null;
        this._myLocationLng = null;
      }

      const topResult = this._stateService.getLastTopSearchResponse();
      let searchResultsType = topResult ? topResult.cssClass : this.searchResultsType;
      let keyword;
      let defaultCategory;
      keyword = topResult ? topResult.name : this._keywordInput;
      defaultCategory = topResult ? topResult.data.defaultCategory : this._defaultCategory;
      if (topResult && topResult.data.defaultCategory === '') {
        defaultCategory = topResult.data.type.toLowerCase();
      }
      if (
        topResult &&
        (topResult.data.type.toLowerCase() === SettingsService.searchResultsType.people ||
          topResult.data.type.toLowerCase() === SettingsService.searchResultsType.places)
      ) {
        keyword = this._keywordInput;
        searchResultsType = topResult.data.type;
        defaultCategory = searchResultsType;
      }
      const selectedResult = this.keywordAutocompleteData && this.keywordAutocompleteData.find((x) => x.selected);
      const selectedType = selectedResult && selectedResult.data.type.replace('header-', '').replace('Header', '');
      if (selectedType) {
        keyword = this._keywordInput;
        searchResultsType = selectedType.toLowerCase();
        defaultCategory = selectedResult.data.defaultCategory
          ? selectedResult.data.defaultCategory
          : selectedResult.data.name;
      }
      const previousSelected = this._stateService.getLastSelected();
      if (previousSelected && previousSelected.searchTerm) {
        keyword = previousSelected.searchTerm;
        searchResultsType = previousSelected.autocompleteUiItem.data.type.toLowerCase();
        defaultCategory = previousSelected.autocompleteUiItem.data.defaultCategory
          ? previousSelected.autocompleteUiItem.data.defaultCategory
          : previousSelected.autocompleteUiItem.data.name;
      }
      if (
        defaultCategory &&
        defaultCategory.toLowerCase() !== SettingsService.searchResultsType.places &&
        defaultCategory.toLowerCase() !== SettingsService.searchResultsType.people
      ) {
        defaultCategory = SettingsService.searchResultsType.places;
      }
      if (defaultCategory) {
        defaultCategory = defaultCategory.toLowerCase();
      }
      this._stateService.saveLastSearchType(searchResultsType);
      this._stateService.saveLastDefaultCategory(defaultCategory);

      if (!topResult && !previousSelected) {
        this.onAutocompleteSkipped(keyword);
        return;
      }

      const routeUrl: RouteUrl = this._helperService.getRoute(
        RouterHelperService.routeType.searchResults,
        null,
        null,
        defaultCategory ? defaultCategory.toLowerCase() : defaultCategory,
        this._helperService.stripSearchTermPrefix(keyword),
        this._locationInput,
        searchResultsType ? searchResultsType.toLowerCase() : searchResultsType,
        this._myLocationLat,
        this._myLocationLng,
        this.myLocationLabel,
      );

      // This is triggered when the "find" button is clicked
      // Will save the query string collection to the newly added searchParameters and then later remove queryParams from navigate()
      this._stateService.setSearchQueryParameters(routeUrl.query);

      void this._router.navigate(routeUrl.link);
      this._uiService.scrollToTop();
      this._stateService.findClick();
    });
  }

  onHomeClick() {
    this.keywordsAutocompleteComponent.resetInputValue('');
    this.locationAutocompleteComponent.resetInputValue('');
    this.onMyLocationClick();
  }

  /**
   * Fired when keywords input changes in the child autocomplete component,
   * child component returns the input without a delay unlike the onKeywordsGetData event.
   * We need the input data without a delay in order to make the Find button work well when its clicked or when Enter key is pressed.
   */
  onKeywordsInput(event: string) {
    this._keywordInput = event;
    // Reset search type on a new lookup. If user is typing fast and no data comes back before they fire the search
    // we don't want to keep searchResultsType value from the previous search.
    this.searchResultsType = null;
    this._stateService.clearLastSelected();
  }

  async onKeywordsGetData() {
    this._stateService.clearLastTopSearchResponse();
    if (this._keywordInput) {
      const keywordInput = this._helperService.stripSearchTermPrefix(this._keywordInput);
      if (this.usingMyLocation && this._myLocationLat && this._myLocationLng) {
        this.doKeywordLookup(new SearchRequest(keywordInput, null, this._myLocationLat, this._myLocationLng));
      } else {
        await this.setLatLngFromLocation();
        this.doKeywordLookup(
          new SearchRequest(keywordInput, this._locationInput, this._myLocationLat, this._myLocationLng),
        );
      }
    }
  }

  onLocationInput(event: string) {
    this._locationInput = event;

    // If location input is empty then show the MyLocation option.
    if (!event) {
      this.showMyLocationOption();
    }
  }

  async onLocationGetData() {
    if (this._locationInput) {
      await this.setLatLngFromLocation();
      this.doLocationLookup(new SearchRequest(null, this._locationInput, this._myLocationLat, this._myLocationLng));
    }
  }

  async onKeywordsInputFocus(event: boolean) {
    await this.onKeywordsGetData();
  }

  onLocationInputFocus(event: boolean) {
    // if in focus show my location option
    if (event) {
      this.showMyLocationOption();
    }
  }

  private showMyLocationOption() {
    const data: AutocompleteUiItem[] = [];

    if (!this._locationInput && navigator.geolocation) {
      const myLocationUiItem = this.buildCustomAutocompleteUiItem(
        SettingsService.autoCompleteValues.useMyLocationText,
        this._helperService.autocompleteItemType.MyLocation.type,
        null,
        null,
        0,
      );

      data.push(myLocationUiItem);
      this.locationAutocompleteData = data;
    }
  }

  /**
   * Get data from the service to populate the autocomplete dropdown.
   * @param request - Search request object
   */
  private doKeywordLookup(request: SearchRequest) {
    const storeResult = this._stateService.getLastTopSearchResponse();
    const keyword = storeResult ? storeResult.name : this._keywordInput;
    this.searchResultsType = storeResult ? storeResult.cssClass : this.searchResultsType;
    if (storeResult) {
      this._defaultCategory = storeResult ? storeResult.data.defaultCategory : this._defaultCategory;
      if (storeResult.data.defaultCategory === '') {
        this._defaultCategory = storeResult.data.type.toLowerCase();
      }
      this.onFindClick();
      this._stateService.clearLastTopSearchResponse();
    }

    this._searchService.keywordLookup(request).subscribe(
      (response: AutocompleteSearchResponse) => {
        // If data comes back but user has cleared the input box then don't show the data that came back,
        // i.e. don't show the autocomplete dropdown if the input is already blank.
        // It was happening when data retrieval was a bit slow and you press the backspace to clear the input,
        // by the time data came back the input was already blank and the data shouldn't have been displayed.
        if (!keyword) {
          this.keywordAutocompleteData = [];
          return;
        }

        // If there are results then populate the auto complete data.
        if (response && response.results) {
          const data = response.results.map((item, index) => this.buildAutocompleteItem(item, index));

          if (!data || data.length <= 0) {
            // Add 'no results' item to the data
            const noDataUiItem = this.buildCustomAutocompleteUiItem(
              SettingsService.autoCompleteValues.noResultsText,
              this._helperService.autocompleteItemType.NoResults.type,
              keyword,
              this._locationInput,
              0,
            );
            data.push(noDataUiItem);

            const input = keyword;
            const locationInput = this._locationInput ? this._locationInput : null;
            this._gaService.sendNoAutocompleteResultsEvent(input, locationInput);
          } else {
            // if there is data, grab the first item type and use it as a default search type, this is for the Find button,
            // when nothing was selected from the dropdown and the Find button was clicked.
            const searchType = this._stateService.getLastSearchType();
            this.searchResultsType = searchType;
          }

          this.keywordAutocompleteData = data;
        }
      },
      () => {
        // Show an error message if there is a connection error.
        // We may not want to show an error for the dropdown list in case if there are intermittent connectivity issues,
        // users can use the Find button to go to the search results page and if there is still a connection issue then
        // they will see a friendly error message. We will need to handle errors on the search results page.
        // this._errorHandlingService.friendlyErrorHandler(true);
      },
    );
  }

  private doLocationLookup(request: SearchRequest) {
    const searchInput = this._keywordInput ? this._keywordInput : null;
    this._searchService.locationLookup(request).subscribe(
      (response: AutocompleteSearchResponse) => {
        // If the input is blank (edited by the user) by the time we recieved the data then don't show the data.
        if (!this._locationInput) {
          this.locationAutocompleteData = [];
          return;
        }

        if (response && response.results) {
          const data = response.results.map((item, index) => this.buildAutocompleteItem(item, index));

          // Add 'no results' item to the data
          if (!data || data.length === 0) {
            const noDataUiItem = this.buildCustomAutocompleteUiItem(
              SettingsService.autoCompleteValues.noResultsText,
              this._helperService.autocompleteItemType.NoResults.type,
              this._locationInput, // Location is the keyword in this case so the helper can generate the correct string
              null,
              0,
            );
            data.push(noDataUiItem);

            this._gaService.sendNoLocationResultsEvent(searchInput, this._locationInput);
          }

          // Add my location option as the last item of the dropdown list so the my location option is always available
          const myLocationUiItem = this.buildCustomAutocompleteUiItem(
            SettingsService.autoCompleteValues.useMyLocationText,
            this._helperService.autocompleteItemType.MyLocation.type,
            null,
            null,
            0,
          );

          data.push(myLocationUiItem);

          this.locationAutocompleteData = data;
        }
      },
      () => {
        // Error handling is disabled for now, see explanation in the comments above.
        // this._errorHandlingService.friendlyErrorHandler(true);
      },
    );
  }

  /**  Helper to add custom autocomplete items */
  private buildCustomAutocompleteUiItem(
    name: string,
    type: string,
    keyword: string,
    location: string,
    index: number,
  ): AutocompleteUiItem {
    const item = new WebApiModel.AutocompleteItem();

    let itemText = name;

    if (keyword) {
      itemText += ` for '${keyword}'`;

      if (location) {
        itemText += ` in '${location}'`;
      }
    }

    item.name = itemText;
    item.type = type;
    const uiItem = this.buildAutocompleteItem(item, index);
    return uiItem;
  }

  /**
   * Stop using my location
   */
  onMyLocationClick() {
    // Turn off search by my location and reset the coordinates
    this.usingMyLocation = false;
    this.myLocationFound = false;
    this.failedMyLocation = false;
    this._myLocationLat = null;
    this._myLocationLng = null;
  }

  async getMyLocation() {
    this.failedMyLocation = false;
    if (!navigator.geolocation) {
      return;
    }
    this.lookingForLocation = true;
    this.myLocationFound = false;
    this.myLocationLabel = SettingsService.autoCompleteValues.lookingForMyLocationText;

    try {
      const currentPosition: any = await this.getCurrentPosition();
      this.lookingForLocation = false;
      if (currentPosition && currentPosition.coords) {
        this.myLocationFound = true;
        this._myLocationLat = currentPosition.coords.latitude;
        this._myLocationLng = currentPosition.coords.longitude;
        // The locationTypes array is ordered, the order in which the items appears, is the order of specifity for the search:
        // first sublocality, then postal_town etc.
        const locationTypes = [
          'sublocality',
          'postal_town',
          'administrative_area_level_2',
          'administrative_area_level_1',
        ];
        let location: any;
        for (const locationType of locationTypes) {
          location = await this.getLocationAddress(this._myLocationLat, this._myLocationLng, locationType);
          if (location) {
            break;
          }
        }
        if (!location) {
          this.setLocationNotFound();
          return;
        }
        this.myLocationLabel = location.long_name;
        const searchInput = this._keywordInput ? this._keywordInput : null;
        this._gaService.sendLocationResultsEvent(searchInput, this.myLocationLabel, true);
      }
    } catch (exception) {
      this.setLocationNotFound();
    }
  }

  /**
   * Helper to reset the state of the UI when not location has been found with location detection
   */
  private setLocationNotFound() {
    this.lookingForLocation = false;
    this.myLocationFound = false;
    this.failedMyLocation = true;
    this._myLocationLat = null;
    this._myLocationLng = null;
    this.myLocationLabel = SettingsService.autoCompleteValues.failedMyLocation;
  }

  // Retrieve lat/lng of specified location
  private async setLatLngFromLocation() {
    if (this._locationInput && this._locationInput.length > 0) {
      const loc = await this._mapService.getLocationFromAddress(this._locationInput + ', New Zealand');

      if (loc) {
        this._myLocationLat = loc.lat.toString();
        this._myLocationLng = loc.lng.toString();
      }
    }
  }

  public async getLocationAddress(latitude, longitude, searchType) {
    const latlng = new google.maps.LatLng(latitude, longitude);
    try {
      const results: any = await this.geocode({ latLng: latlng });
      const filteredResults = results.filter((result) => result.types.some((type) => type === searchType));
      const filteredAddress = filteredResults.find((filteredResult) =>
        filteredResult
          ? filteredResult.address_components.filter((passedAddressComponent) =>
              passedAddressComponent.types.find((type) => type === searchType),
            )
          : undefined,
      );
      const addressComponent = filteredAddress
        ? filteredAddress.address_components.find((passedAddressComponent) =>
            passedAddressComponent.types.some((type) => type === searchType),
          )
        : undefined;
      if (addressComponent) {
        return addressComponent;
      }
      return;
    } catch (exception) {
      this.setLocationNotFound();
    }
  }

  /**
   * Helper/wrapper to allow async/await for navigator.geolocation.getCurrentPosition
   * @param options the options that would have been sent to navigator.geolocation.getCurrentPosition - see
   * https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/getCurrentPosition
   */
  public getCurrentPosition(options = { enableHighAccuracy: true, maximumAge: 0 }) {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  }

  /**
   * Helper/wrapper to allow async/await for geocoder.geocode
   * @param options the options that would have been sent to geocoder.geocode - see
   * https://angular-maps.com/api-docs/agm-core/injectables/agmgeocoder
   */
  public geocode(options = {}) {
    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(options, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          if (results.length) {
            resolve(results);
          } else {
            reject(new Error('No results found'));
          }
        } else {
          reject(new Error('Geocoder failed due to: ' + status));
        }
      });
    });
  }

  onAutocompleteSkipped(keyword: string) {
    const request = {
      keywords: keyword,
      latitude: null,
      longitude: null,
      location: null,
    };
    this._stateService.clearLastSelected();
    this._stateService.isSavingTopResult.next(true);
    this._searchService.keywordLookup(request).subscribe(
      (response: AutocompleteSearchResponse) => {
        // If there are results then populate the auto complete data.
        if (response && response.results && response.results.length) {
          const data = response.results.map((item, index) => this.buildAutocompleteItem(item, index));

          if (!data || data.length <= 0) {
            const input = this._keywordInput;
            const locationInput = this._locationInput ? this._locationInput : null;
            this._gaService.sendNoAutocompleteResultsEvent(input, locationInput);
            const noDataUiItem = this.buildCustomAutocompleteUiItem(
              input,
              this._helperService.autocompleteItemType.NoResults.type,
              this._locationInput, // Location is the keyword in this case so the helper can generate the correct string
              null,
              0,
            );
            this._stateService.saveLastTopSearchResponse(noDataUiItem);
            this._stateService.isSavingTopResult.next(false);
          } else {
            // if there is data, grab the first item type and use it as a default search type, this is for the Find button,
            // when nothing was selected from the dropdown and the Find button was clicked.
            if (data && data[1]) {
              this._stateService.saveLastTopSearchResponse(data[1]);
              this._stateService.isSavingTopResult.next(false);
            }
            this.doKeywordLookup(request);
          }
        } else {
          const noDataUiItem = this.buildCustomAutocompleteUiItem(
            this._keywordInput,
            this._helperService.autocompleteItemType.NoResults.type,
            this._locationInput, // Location is the keyword in this case so the helper can generate the correct string
            null,
            0,
          );
          this._stateService.saveLastTopSearchResponse(noDataUiItem);
          this._stateService.isSavingTopResult.next(false);
          this.onFindClick();
        }
      },
      () => {
        // Show an error message if there is a connection error.
        // We may not want to show an error for the dropdown list in case if there are intermittent connectivity issues,
        // users can use the Find button to go to the search results page and if there is still a connection issue then
        // they will see a friendly error message. We will need to handle errors on the search results page.
        // this._errorHandlingService.friendlyErrorHandler(true);
      },
    );
  }
}
